import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, AlertCircle, Calendar, ArrowLeft, RefreshCw, CheckSquare, Square, Trash2 } from 'lucide-react';
import { DevOpsTask, DevOpsTaskStatus, DevOpsTaskPriority, DevOpsTaskType } from '../../types/devopsTasks';
import { User as UserType } from '../../types';
import Avatar from '../common/Avatar';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

interface TaskListProps {
  currentUser: UserType;
  onSelectTask: (taskId: string) => void;
  onBack?: () => void;
}

const TaskList: React.FC<TaskListProps> = ({ currentUser, onSelectTask, onBack }) => {
  const [tasks, setTasks] = useState<DevOpsTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterPriority, setFilterPriority] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string[]>([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  // Bulk operations
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [bulkActionValue, setBulkActionValue] = useState('');
  const [bulkActionType, setBulkActionType] = useState<'status' | 'priority'>('status');
  const [bulkApplying, setBulkApplying] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (priorityDropdownRef.current && !priorityDropdownRef.current.contains(event.target as Node)) {
        setShowPriorityDropdown(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/tasks');
      if (!response.ok) throw new Error('Failed to fetch tasks');
      const data = await response.json();
      setTasks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredTasks.map(t => t._id!).filter(Boolean);
    const allSelected = visibleIds.every(id => selectedTaskIds.includes(id));
    setSelectedTaskIds(allSelected ? [] : visibleIds);
  };

  const handleApplyBulkAction = async () => {
    if (selectedTaskIds.length === 0 || !bulkActionValue) return;
    try {
      setBulkApplying(true);
      const operation = bulkActionType === 'status' ? 'update-status' : 'update-priority';
      const data = bulkActionType === 'status'
        ? { status: bulkActionValue, changedBy: currentUser.id, changedByName: currentUser.name || currentUser.email }
        : { priority: bulkActionValue };

      const response = await fetch('/api/tasks/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskIds: selectedTaskIds, operation, data })
      });
      if (!response.ok) throw new Error(await readApiError(response, 'Failed to apply bulk action'));

      setSelectedTaskIds([]);
      setBulkActionValue('');
      await fetchTasks();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to apply bulk action', 'error');
    } finally {
      setBulkApplying(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTaskIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedTaskIds.length} selected task(s)? This cannot be undone.`)) return;
    try {
      setBulkApplying(true);
      const response = await fetch('/api/tasks/bulk-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskIds: selectedTaskIds, operation: 'delete' })
      });
      if (!response.ok) throw new Error(await readApiError(response, 'Failed to delete selected tasks'));

      setSelectedTaskIds([]);
      await fetchTasks();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete selected tasks', 'error');
    } finally {
      setBulkApplying(false);
    }
  };

  // Filter options
  const statusOptions = ['new', 'assigned', 'in-progress', 'testing', 'completed', 'rejected'];
  const priorityOptions = ['critical', 'high', 'medium', 'low'];
  const typeOptions = [
    { value: 'server-issue', label: 'Server Issue' },
    { value: 'environment-setup', label: 'Environment Setup' },
    { value: 'configuration', label: 'Configuration' },
    { value: 'bug-investigation', label: 'Bug Investigation' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'other', label: 'Other' }
  ];

  // Toggle filter selection
  const toggleStatusFilter = (status: string) => {
    setFilterStatus(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const togglePriorityFilter = (priority: string) => {
    setFilterPriority(prev => 
      prev.includes(priority) ? prev.filter(p => p !== priority) : [...prev, priority]
    );
  };

  const toggleTypeFilter = (type: string) => {
    setFilterType(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  // Select All / Deselect All
  const toggleAllStatus = () => {
    if (filterStatus.length === statusOptions.length) {
      setFilterStatus([]);
    } else {
      setFilterStatus([...statusOptions]);
    }
  };

  const toggleAllPriority = () => {
    if (filterPriority.length === priorityOptions.length) {
      setFilterPriority([]);
    } else {
      setFilterPriority([...priorityOptions]);
    }
  };

  const toggleAllType = () => {
    const allTypeValues = typeOptions.map(t => t.value);
    if (filterType.length === allTypeValues.length) {
      setFilterType([]);
    } else {
      setFilterType([...allTypeValues]);
    }
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = searchQuery === '' || 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.taskNumber.toString().toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = filterStatus.length === 0 || filterStatus.includes(task.status);
    const matchesPriority = filterPriority.length === 0 || filterPriority.includes(task.priority);
    const matchesType = filterType.length === 0 || filterType.includes(task.taskType);

    return matchesSearch && matchesStatus && matchesPriority && matchesType;
  });

  // Priority badge colors
  const getPriorityColor = (priority: DevOpsTaskPriority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Status badge colors
  const getStatusColor = (status: DevOpsTaskStatus) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-indigo-100 text-indigo-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'testing': return 'bg-cyan-100 text-cyan-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get task type emoji
  const getTypeEmoji = (type: DevOpsTaskType) => {
    switch (type) {
      case 'server-issue': return '🔴';
      case 'environment-setup': return '🌐';
      case 'configuration': return '⚙️';
      case 'bug-investigation': return '🐛';
      case 'infrastructure': return '🏗️';
      case 'maintenance': return '🔧';
      case 'other': return '📝';
      default: return '📋';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
        <span className="text-red-800">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">All DevOps Tasks</h1>
        <p className="text-gray-600 mt-1">View and manage all DevOps tasks</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative" ref={statusDropdownRef}>
            <button
              onClick={() => {
                setShowStatusDropdown(!showStatusDropdown);
                setShowPriorityDropdown(false);
                setShowTypeDropdown(false);
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex items-center justify-between transition-all ${
                filterStatus.length > 0 
                  ? 'bg-blue-50 border-blue-300 text-blue-700' 
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <span className="font-medium">
                {filterStatus.length === 0 ? 'All Status' : `Status (${filterStatus.length})`}
              </span>
              <Filter className={`w-4 h-4 ${filterStatus.length > 0 ? 'text-blue-500' : 'text-gray-400'}`} />
            </button>
            {showStatusDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
                {/* Select All */}
                <div className="sticky top-0 bg-gradient-to-b from-gray-50 to-white border-b border-gray-200 px-3 py-2">
                  <label className="flex items-center cursor-pointer hover:bg-gray-100 rounded px-2 py-1.5 transition-colors">
                    <input
                      type="checkbox"
                      checked={filterStatus.length === statusOptions.length}
                      onChange={toggleAllStatus}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-semibold text-gray-900">
                      {filterStatus.length === statusOptions.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </label>
                </div>
                {/* Options */}
                <div className="overflow-y-auto max-h-56">
                  {statusOptions.map(status => (
                    <label key={status} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors border-l-2 border-transparent hover:border-blue-500">
                      <input
                        type="checkbox"
                        checked={filterStatus.includes(status)}
                        onChange={() => toggleStatusFilter(status)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className={`ml-3 text-sm capitalize ${filterStatus.includes(status) ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                        {status === 'in-progress' ? 'In Progress' : status}
                      </span>
                      {filterStatus.includes(status) && (
                        <span className="ml-auto text-blue-600">✓</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Priority Filter */}
          <div className="relative" ref={priorityDropdownRef}>
            <button
              onClick={() => {
                setShowPriorityDropdown(!showPriorityDropdown);
                setShowStatusDropdown(false);
                setShowTypeDropdown(false);
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex items-center justify-between transition-all ${
                filterPriority.length > 0 
                  ? 'bg-blue-50 border-blue-300 text-blue-700' 
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <span className="font-medium">
                {filterPriority.length === 0 ? 'All Priority' : `Priority (${filterPriority.length})`}
              </span>
              <Filter className={`w-4 h-4 ${filterPriority.length > 0 ? 'text-blue-500' : 'text-gray-400'}`} />
            </button>
            {showPriorityDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                {/* Select All */}
                <div className="sticky top-0 bg-gradient-to-b from-gray-50 to-white border-b border-gray-200 px-3 py-2">
                  <label className="flex items-center cursor-pointer hover:bg-gray-100 rounded px-2 py-1.5 transition-colors">
                    <input
                      type="checkbox"
                      checked={filterPriority.length === priorityOptions.length}
                      onChange={toggleAllPriority}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-semibold text-gray-900">
                      {filterPriority.length === priorityOptions.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </label>
                </div>
                {/* Options */}
                <div>
                  {priorityOptions.map(priority => (
                    <label key={priority} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors border-l-2 border-transparent hover:border-blue-500">
                      <input
                        type="checkbox"
                        checked={filterPriority.includes(priority)}
                        onChange={() => togglePriorityFilter(priority)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className={`ml-3 text-sm capitalize ${filterPriority.includes(priority) ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                        {priority}
                      </span>
                      {filterPriority.includes(priority) && (
                        <span className="ml-auto text-blue-600">✓</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Type Filter */}
          <div className="relative" ref={typeDropdownRef}>
            <button
              onClick={() => {
                setShowTypeDropdown(!showTypeDropdown);
                setShowStatusDropdown(false);
                setShowPriorityDropdown(false);
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left flex items-center justify-between transition-all ${
                filterType.length > 0 
                  ? 'bg-blue-50 border-blue-300 text-blue-700' 
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <span className="font-medium">
                {filterType.length === 0 ? 'All Types' : `Types (${filterType.length})`}
              </span>
              <Filter className={`w-4 h-4 ${filterType.length > 0 ? 'text-blue-500' : 'text-gray-400'}`} />
            </button>
            {showTypeDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
                {/* Select All */}
                <div className="sticky top-0 bg-gradient-to-b from-gray-50 to-white border-b border-gray-200 px-3 py-2">
                  <label className="flex items-center cursor-pointer hover:bg-gray-100 rounded px-2 py-1.5 transition-colors">
                    <input
                      type="checkbox"
                      checked={filterType.length === typeOptions.length}
                      onChange={toggleAllType}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm font-semibold text-gray-900">
                      {filterType.length === typeOptions.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </label>
                </div>
                {/* Options */}
                <div className="overflow-y-auto max-h-64">
                  {typeOptions.map(type => (
                    <label key={type.value} className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors border-l-2 border-transparent hover:border-blue-500">
                      <input
                        type="checkbox"
                        checked={filterType.includes(type.value)}
                        onChange={() => toggleTypeFilter(type.value)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className={`ml-3 text-sm ${filterType.includes(type.value) ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                        {type.label}
                      </span>
                      {filterType.includes(type.value) && (
                        <span className="ml-auto text-blue-600">✓</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results count and Clear Filters */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredTasks.length}</span> of <span className="font-semibold text-gray-900">{tasks.length}</span> tasks
          </p>
          {(searchQuery || filterStatus.length > 0 || filterPriority.length > 0 || filterType.length > 0) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterStatus([]);
                setFilterPriority([]);
                setFilterType([]);
                setShowStatusDropdown(false);
                setShowPriorityDropdown(false);
                setShowTypeDropdown(false);
              }}
              className="flex items-center gap-2 px-4 py-1.5 text-sm text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-600 rounded-lg font-medium transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Clear All Filters
            </button>
          )}
        </div>
        
        {/* Active Filters Display */}
        {(filterStatus.length > 0 || filterPriority.length > 0 || filterType.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {filterStatus.map(status => (
              <span key={status} className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                Status: {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                <button
                  onClick={() => toggleStatusFilter(status)}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                >
                  ×
                </button>
              </span>
            ))}
            {filterPriority.map(priority => (
              <span key={priority} className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                Priority: {priority.charAt(0).toUpperCase() + priority.slice(1)}
                <button
                  onClick={() => togglePriorityFilter(priority)}
                  className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                >
                  ×
                </button>
              </span>
            ))}
            {filterType.map(type => (
              <span key={type} className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                Type: {typeOptions.find(t => t.value === type)?.label}
                <button
                  onClick={() => toggleTypeFilter(type)}
                  className="hover:bg-green-200 rounded-full p-0.5 transition-colors"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedTaskIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-blue-900">
            {selectedTaskIds.length} task(s) selected
          </span>

          <select
            value={bulkActionType}
            onChange={(e) => { setBulkActionType(e.target.value as 'status' | 'priority'); setBulkActionValue(''); }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="status">Change Status</option>
            <option value="priority">Change Priority</option>
          </select>

          <select
            value={bulkActionValue}
            onChange={(e) => setBulkActionValue(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">-- Select value --</option>
            {(bulkActionType === 'status' ? statusOptions : priorityOptions).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>

          <button
            onClick={handleApplyBulkAction}
            disabled={!bulkActionValue || bulkApplying}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Apply
          </button>

          <button
            onClick={handleBulkDelete}
            disabled={bulkApplying}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed ml-auto"
          >
            <Trash2 className="w-4 h-4" />
            Delete Selected
          </button>

          <button
            onClick={() => setSelectedTaskIds([])}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Task Table */}
      {filteredTasks.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
          <p className="text-gray-600">
            {searchQuery || filterStatus.length > 0 || filterPriority.length > 0 || filterType.length > 0
              ? 'Try adjusting your filters'
              : 'No tasks have been created yet'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left">
                    <button onClick={toggleSelectAllVisible} title="Select all visible">
                      {filteredTasks.length > 0 && filteredTasks.every(t => t._id && selectedTaskIds.includes(t._id)) ? (
                        <CheckSquare className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Task ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Title & Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Created Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTasks.map((task) => (
                  <tr
                    key={task._id}
                    onClick={() => task._id && onSelectTask(task._id)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    {/* Select checkbox */}
                    <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => task._id && toggleTaskSelection(task._id)}>
                        {task._id && selectedTaskIds.includes(task._id) ? (
                          <CheckSquare className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </td>

                    {/* Task ID */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-xl">{getTypeEmoji(task.taskType)}</span>
                        <span className="text-sm font-mono font-semibold text-blue-600">
                          {task.taskNumber}
                        </span>
                      </div>
                    </td>

                    {/* Title & Description */}
                    <td className="px-4 py-4 max-w-md">
                      <div className="space-y-1">
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">
                          {task.title}
                        </h3>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {task.description}
                        </p>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
                        {task.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </td>

                    {/* Priority */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(task.priority)}`}>
                        {task.priority === 'critical' && '🔴 '}
                        {task.priority === 'high' && '🔥 '}
                        {task.priority === 'medium' && '⚡ '}
                        {task.priority === 'low' && '⬇️ '}
                        {task.priority.toUpperCase()}
                      </span>
                    </td>

                    {/* Created By */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <Avatar name={task.createdBy.name} userId={task.createdBy.id} size="sm" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{task.createdBy.name}</p>
                          <p className="text-xs text-gray-500">{task.createdBy.role || 'DevOps'}</p>
                        </div>
                      </div>
                    </td>

                    {/* Assigned To */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {task.assignedTo ? (
                        <div className="flex items-center space-x-2">
                          <Avatar name={task.assignedTo.name} userId={task.assignedTo.id} size="sm" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{task.assignedTo.name}</p>
                            <p className="text-xs text-gray-500">DevOps</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 italic">Not Assigned</span>
                      )}
                    </td>

                    {/* Created Date */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(task.createdAt)}</div>
                      {task.expectedCompletionDate && (
                        <div className="text-xs text-gray-500 flex items-center mt-1">
                          <Calendar className="w-3 h-3 mr-1" />
                          Due: {formatDate(task.expectedCompletionDate)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer with refresh button */}
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              📋 Displaying {filteredTasks.length} of {tasks.length} total tasks
            </p>
            <button
              onClick={fetchTasks}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskList;
